<script lang="ts">
	//Modules
	import { replace } from 'svelte-spa-router';
	//Services
	import { get_user_pin, pullDataWithLoading, standardLogin } from 'src/services/http';
	import { format } from 'src/services/i18n';

	//Helpers
	import { clearAllData, handleLoginErrors } from 'src/helpers/account_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isSubUnit } from 'src/helpers/user_helpers';
	import { user_data } from 'src/stores/account/user';
	import { mobile } from 'src/services/mobile-rework';
	import { account_switch_mobile_android } from 'src/services/native_container_requests_android';
	import { account_switch_mobile_ios } from 'src/services/native_container_requests_ios';
	import { removeProfile } from 'src/helpers/login_helpers';
	import { profiles } from 'src/stores/features/profiles';
	import { socket_disconnect } from 'src/services/socket';

	//Design system components
	import { Alert, ButtonV2, Stack, toast, Text } from '@Shelly/shelly-design-system';

	//Components
	import DeleteAccountModal from 'src/routes/DeleteAccountModalV2.svelte';
	//Types
	import type { MenuListDataType } from 'src/components/Profiles/DropdownItem.types';

	$: isSubAccount = isSubUnit();

	let showModal = false;
	let loading = false;
	let alertError = '';

	const inst = $user_data.user_api_url;
	const token = $user_data.token;

	let profileToDelete = null;

	const activeProfile = $profiles.find((item) => item.isSelected);

	const handleDelete = () => {
		showModal = true;
		profileToDelete = $user_data.email;
	};

	const handleLogin = async (email, pass, profileId, sub = undefined) => {
		const res = await standardLogin(email, pass);

		if (res?.isok) {
			if (mobile.isMobile) {
				if (mobile.OS === 'IOS') {
					account_switch_mobile_ios(email);
				} else {
					account_switch_mobile_android(email);
				}
			}

			await pullDataWithLoading(); // calls => handleInitialLoad
			get_user_pin();

			profiles.change(email);
			// Success
			replace('/');
		} else {
			const alertProps = handleLoginErrors(res);
			console.error(JSON.stringify(alertProps));

			//@ToDo: More tests for this specific case
			toast.open({
				type: 'danger',
				title: alertProps.heading,
				text: alertProps.message,
			});
		}
		loading = false;
		showModal = false;
	};

	const callbackSuccess = async () => {
		const nextProfile: void | MenuListDataType = removeProfile(activeProfile.email);

		socket_disconnect();

		if (nextProfile) {
			// will log in with the next profile
			toast.open({
				type: 'success',
				title: capFirstChar($format('alert_title_success', { default: 'Success' })),
				text: capFirstChar(
					$format('alert_text_delete_account_next', {
						values: { accountName: profileToDelete },
						default: `The account ${$user_data.email} is no longer active. Logging you in with the next account.`,
					}),
				),
			});
			handleLogin(nextProfile.email, nextProfile.pass, nextProfile.id, nextProfile.sub);
		} else {
			clearAllData();
			loading = false;
			showModal = false;
			toast.open({
				type: 'success',
				title: capFirstChar($format('alert_title_success', { default: 'Success' })),
				text: capFirstChar(
					$format('alert_text_delete_account', {
						values: { accountName: profileToDelete },
						default: `The account ${$user_data.email} is no longer active.`,
					}),
				),
			});
		}
	};

	const callbackError = (error) => {
		console.error('error on delete account', error);
		alertError = error;
	};
</script>

{#if alertError}
	<Alert
		type="danger"
		heading={$format('alert_error_on_', {
			default: 'Error on deleting account!',
		})}
		isHeadingBold
		message={alertError +
			'. ' +
			$format('please_contact_supports', {
				default: 'Please contact support.',
			})}
		class="mb-m"
	/>
{/if}

<Stack
	direction="column"
	directionMobile="row"
	align="between"
	alignMobile="none"
	gap="m"
	class="align-items-center"
>
	<div>
		<Text styleType="body" class="mb-s"
			>{$format('label_delete_account', { default: 'Delete Account' })}</Text
		>
		<Text styleType="body-small" class="text-color-muted">
			{#if isSubAccount}
				{$format('label_delete_sub_account_description', {
					default: 'Remove your access to shared homes and delete your account',
				})}
			{:else}
				{$format('label_delete_account_description_multi_unit', {
					default: 'Delete all your homes, devices, and associated data',
				})}
			{/if}
		</Text>
	</div>

	<ButtonV2 size="m" variant="secondary" on:click={handleDelete}>
		{capFirstChar(
			$format('button_delete_account', {
				default: 'Delete Account',
			}),
		)}
	</ButtonV2>
</Stack>

<DeleteAccountModal bind:showModal {inst} {token} bind:loading {callbackSuccess} {callbackError} />
